import React, { useState } from "react";
import {
  XMarkIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ShieldCheckIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { formatNumber } from "../../utils/formatters";
import { useMyAds, usePaymentMethods } from "../../hooks/useP2P";
import { toast } from "react-toastify";

const P2POrderForm = ({ onClose, onAdCreated, type, asset, fiat }) => {
  const { createAd, loading } = useMyAds();
  const { paymentMethods: backendPaymentMethods, loading: methodsLoading } =
    usePaymentMethods();

  const [formData, setFormData] = useState({
    type: type || "sell",
    asset: asset || "USDT",
    fiat: fiat || "USD",
    price: "",
    totalAmount: "",
    minLimit: "",
    maxLimit: "",
    paymentMethods: [],
    paymentTimeLimit: "15",
    paymentAccountInfo: "",
    terms: "",
    autoReply: "",
    requiresVerification: false,
  });

  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});

  const assets = ["USDT", "BTC", "BTC1", "ETH", "BNB", "BUSD"];
  const fiats = [
    { code: "USD", name: "US Dollar", symbol: "$" },
    { code: "EUR", name: "Euro", symbol: "€" },
    { code: "GBP", name: "British Pound", symbol: "£" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  ];

  // Use backend payment methods or fallback to static list
  const paymentMethods =
    backendPaymentMethods?.length > 0
      ? backendPaymentMethods.map((method) => ({
          id: method.code,
          name: method.name,
          icon: method.icon || "💳",
        }))
      : [
          { id: "bank_transfer", name: "Bank Transfer", icon: "🏦" },
          { id: "paypal", name: "PayPal", icon: "💳" },
          { id: "wise", name: "Wise", icon: "💰" },
          { id: "revolut", name: "Revolut", icon: "📱" },
          { id: "credit_card", name: "Credit Card", icon: "💳" },
          { id: "cash", name: "Cash", icon: "💵" },
          { id: "zelle", name: "Zelle", icon: "💸" },
          { id: "venmo", name: "Venmo", icon: "📲" },
        ];

  const paymentTimeLimits = [
    { value: "15", label: "15 minutes" },
    { value: "30", label: "30 minutes" },
    { value: "45", label: "45 minutes" },
    { value: "60", label: "1 hour" },
    { value: "120", label: "2 hours" },
    { value: "360", label: "6 hours" },
    { value: "720", label: "12 hours" },
    { value: "1440", label: "24 hours" },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const togglePaymentMethod = (methodId) => {
    setFormData((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.includes(methodId)
        ? prev.paymentMethods.filter((id) => id !== methodId)
        : [...prev.paymentMethods, methodId],
    }));
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.price) newErrors.price = "Price is required";
      if (!formData.totalAmount)
        newErrors.totalAmount = "Total amount is required";
      if (!formData.minLimit) newErrors.minLimit = "Minimum limit is required";
      if (!formData.maxLimit) newErrors.maxLimit = "Maximum limit is required";

      if (
        formData.minLimit &&
        formData.maxLimit &&
        parseFloat(formData.minLimit) >= parseFloat(formData.maxLimit)
      ) {
        newErrors.maxLimit = "Maximum limit must be greater than minimum limit";
      }
    }

    if (stepNumber === 2) {
      if (formData.paymentMethods.length === 0) {
        newErrors.paymentMethods = "At least one payment method is required";
      }
      if (!formData.paymentAccountInfo.trim()) {
        newErrors.paymentAccountInfo = "Payment account details are required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (validateStep(2)) {
      try {
        // Map frontend payment method IDs to backend payment method objects
        const selectedPaymentMethods =
          backendPaymentMethods
            ?.filter((method) => formData.paymentMethods.includes(method.code))
            ?.map((method) => method.id) || [];

        const adData = {
          type: formData.type,
          asset: formData.asset,
          fiat_currency: formData.fiat,
          price: parseFloat(formData.price),
          total_amount: parseFloat(formData.totalAmount),
          available_amount: parseFloat(formData.totalAmount),
          min_order_amount: parseFloat(formData.minLimit),
          max_order_amount: parseFloat(formData.maxLimit),
          payment_methods: selectedPaymentMethods,
          payment_time_limit_minutes: parseInt(formData.paymentTimeLimit),
          payment_account_info: formData.paymentAccountInfo,
          terms_and_conditions: formData.terms,
          auto_reply_message: formData.autoReply,
          require_kyc_verified: formData.requiresVerification,
          price_type: "fixed",
        };

        await createAd(adData);
        toast.success("Advertisement created successfully!");

        // Call the callback to refresh ads list
        if (onAdCreated) {
          onAdCreated();
        }

        onClose();
      } catch (error) {
        console.error("Error creating ad:", error);
        toast.error("Failed to create advertisement");
      }
    }
  };

  const calculateValues = () => {
    const price = parseFloat(formData.price) || 0;
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const minLimit = parseFloat(formData.minLimit) || 0;
    const maxLimit = parseFloat(formData.maxLimit) || 0;

    const totalValue =
      formData.type === "sell" ? totalAmount * price : totalAmount;
    const minValue = formData.type === "sell" ? minLimit * price : minLimit;
    const maxValue = formData.type === "sell" ? maxLimit * price : maxLimit;

    return { totalValue, minValue, maxValue };
  };

  const { totalValue, minValue, maxValue } = calculateValues();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[70vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">
              Create {formData.type === "buy" ? "Buy" : "Sell"} Advertisement
            </h2>
            <p className="text-white text-sm">
              Step {step} of 2 -{" "}
              {step === 1 ? "Set Price & Amount" : "Payment & Terms"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              {/* Asset & Fiat Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    I want to {formData.type}
                  </label>
                  <select
                    value={formData.asset}
                    onChange={(e) => handleInputChange("asset", e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {assets.map((asset) => (
                      <option key={asset} value={asset}>
                        {asset}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {formData.type === "buy" ? "Pay with" : "Receive"}
                  </label>
                  <select
                    value={formData.fiat}
                    onChange={(e) => handleInputChange("fiat", e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {fiats.map((fiat) => (
                      <option key={fiat.code} value={fiat.code}>
                        {fiat.symbol} {fiat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange("type", e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sell">Sell</option>
                    <option value="buy">Buy</option>
                  </select>
                </div>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Price per {formData.asset} ({formData.fiat})
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    placeholder="Enter price"
                    className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.price ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white">
                    {formData.fiat}
                  </span>
                </div>
                {errors.price && (
                  <p className="text-red-400 text-sm mt-1">{errors.price}</p>
                )}
              </div>

              {/* Total Amount */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Total Amount ({formData.asset})
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.000001"
                    value={formData.totalAmount}
                    onChange={(e) =>
                      handleInputChange("totalAmount", e.target.value)
                    }
                    placeholder="Enter total amount"
                    className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.totalAmount ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white">
                    {formData.asset}
                  </span>
                </div>
                {errors.totalAmount && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors.totalAmount}
                  </p>
                )}
                {totalValue > 0 && (
                  <p className="text-white text-sm mt-1">
                    Total value: {formatNumber(totalValue, 2)} {formData.fiat}
                  </p>
                )}
              </div>

              {/* Order Limits */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Minimum Limit ({formData.fiat})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.minLimit}
                    onChange={(e) =>
                      handleInputChange("minLimit", e.target.value)
                    }
                    placeholder="Min order amount"
                    className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.minLimit ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  {errors.minLimit && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.minLimit}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Maximum Limit ({formData.fiat})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.maxLimit}
                    onChange={(e) =>
                      handleInputChange("maxLimit", e.target.value)
                    }
                    placeholder="Max order amount"
                    className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.maxLimit ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  {errors.maxLimit && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.maxLimit}
                    </p>
                  )}
                </div>
              </div>

              {/* Summary */}
              {minValue > 0 && maxValue > 0 && (
                <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
                  <h4 className="font-medium text-white mb-2">Order Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white">Min Order:</span>
                      <span className="text-white ml-2">
                        {formatNumber(minValue, 2)} {formData.fiat}
                      </span>
                    </div>
                    <div>
                      <span className="text-white">Max Order:</span>
                      <span className="text-white ml-2">
                        {formatNumber(maxValue, 2)} {formData.fiat}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Payment Methods */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Payment Methods (Select at least one)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => togglePaymentMethod(method.id)}
                      className={`p-3 rounded-lg border transition-all ${
                        formData.paymentMethods.includes(method.id)
                          ? "border-blue-500 bg-blue-900/20 text-blue-400"
                          : "border-gray-600 bg-gray-700 text-white hover:border-gray-500"
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-1">{method.icon}</div>
                        <div className="text-xs font-medium">{method.name}</div>
                      </div>
                      {formData.paymentMethods.includes(method.id) && (
                        <CheckIcon className="h-4 w-4 text-blue-400 absolute top-1 right-1" />
                      )}
                    </button>
                  ))}
                </div>
                {errors.paymentMethods && (
                  <p className="text-red-400 text-sm mt-2">
                    {errors.paymentMethods}
                  </p>
                )}
              </div>

              {/* Payment Time Limit */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Payment Time Limit
                </label>
                <select
                  value={formData.paymentTimeLimit}
                  onChange={(e) =>
                    handleInputChange("paymentTimeLimit", e.target.value)
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {paymentTimeLimits.map((limit) => (
                    <option key={limit.value} value={limit.value}>
                      {limit.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Account Info */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Payment Account Details{" "}
                  <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={formData.paymentAccountInfo}
                  onChange={(e) =>
                    handleInputChange("paymentAccountInfo", e.target.value)
                  }
                  placeholder="Enter your bank details, account number, PayPal email, or payment information buyers/sellers need to send/receive money...

Example:
Bank: Chase Bank
Account Name: John Doe
Account Number: 1234567890
Routing Number: 987654321"
                  rows={6}
                  className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                    errors.paymentAccountInfo
                      ? "border-red-500"
                      : "border-gray-600"
                  }`}
                />
                {errors.paymentAccountInfo && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors.paymentAccountInfo}
                  </p>
                )}
                <p className="text-white text-xs mt-2">
                  ⚠️ Important: Buyers/Sellers will see these details to
                  send/receive payment. Make sure they are accurate.
                </p>
              </div>

              {/* Trading Terms */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Trading Terms and Instructions (Optional)
                </label>
                <textarea
                  value={formData.terms}
                  onChange={(e) => handleInputChange("terms", e.target.value)}
                  placeholder="Enter your trading terms, payment instructions, and any special requirements..."
                  rows={4}
                  className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                    errors.terms ? "border-red-500" : "border-gray-600"
                  }`}
                />
                {errors.terms && (
                  <p className="text-red-400 text-sm mt-1">{errors.terms}</p>
                )}
                <p className="text-white text-xs mt-1">
                  {formData.terms.length}/500 characters
                </p>
              </div>

              {/* Auto Reply */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Auto Reply Message (Optional)
                </label>
                <textarea
                  value={formData.autoReply}
                  onChange={(e) =>
                    handleInputChange("autoReply", e.target.value)
                  }
                  placeholder="Automatic message sent when someone starts a trade with you..."
                  rows={2}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Verification Requirement */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="requiresVerification"
                  checked={formData.requiresVerification}
                  onChange={(e) =>
                    handleInputChange("requiresVerification", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="requiresVerification"
                  className="text-sm text-white"
                >
                  Require verified users only
                </label>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-700">
            <div className="flex items-center space-x-2 text-sm text-white">
              <InformationCircleIcon className="h-4 w-4" />
              <span>Your ad will be visible to all users once created</span>
            </div>

            <div className="flex items-center space-x-3">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-6 py-2 text-white hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors"
                >
                  Back
                </button>
              )}

              {step < 2 ? (
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-green-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Creating..." : "Create Advertisement"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default P2POrderForm;
